import { Validators } from '@angular/forms';

import { z } from 'zod';

import { OptionItem } from '@shared/components/form/components/form-grouped-select/form-grouped-select.types';

import { Assignment, AssignmentType, CategoryType, FormState, MyClassSortColumns } from '../types/tt-my-class.types';

export enum FORM_FIELDS {
  CATEGORY = 'category',
  SELECTED_ACTION = 'selectedAction',
  SELECTED_STUDENTS = 'selectedStudents',
  LIST_FILTER = 'listFilter',
  HIDE_PICTURE_FILTER = 'hidePictureFilter',
  SEARCH_TEXT_FILTER = 'searchTextFilter',
  PAGE_LAYOUT = 'pageLayout',
  ENABLED_SOUND = 'enabledSound',
  SHOW_BEHAVIORS = 'showBehaviors',
  SHOW_CHECK_IN = 'showCheckIn',
  SHOW_FLEXTIME = 'showFlexTime',
  SORT_COLUMN = 'sortColumn',
  SORT_DIRECTION = 'sortDirection',
}

export const FORM = () => ({
  [FORM_FIELDS.CATEGORY]: [CategoryType.MOST_USED, [Validators.required]],
  [FORM_FIELDS.SELECTED_ACTION]: [null, [Validators.required]],
  [FORM_FIELDS.SELECTED_STUDENTS]: [[], [Validators.required]],
  [FORM_FIELDS.LIST_FILTER]: [[]],
  [FORM_FIELDS.HIDE_PICTURE_FILTER]: [false],
  [FORM_FIELDS.SEARCH_TEXT_FILTER]: [''],
  [FORM_FIELDS.PAGE_LAYOUT]: ['grid'],
  [FORM_FIELDS.ENABLED_SOUND]: [false],
  [FORM_FIELDS.SHOW_BEHAVIORS]: [true],
  [FORM_FIELDS.SHOW_CHECK_IN]: [true],
  [FORM_FIELDS.SHOW_FLEXTIME]: [false],
  [FORM_FIELDS.SORT_DIRECTION]: ['asc'],
  // saved preferences will take precedence over this
  [FORM_FIELDS.SORT_COLUMN]: ['lastName'],
});

export enum HALLPASS_FIELDS {
  NOTE = 'note',
  DURATION = 'duration',
  DATE = 'date',
  TIME = 'time',
  APPROVED_BY = 'approvedBy',
}
export const HALLPASS_GROUP = () => ({
  [HALLPASS_FIELDS.NOTE]: [null, [Validators.maxLength(180)]],
  [HALLPASS_FIELDS.DURATION]: [null, [Validators.required]],
  [HALLPASS_FIELDS.DATE]: [null],
  [HALLPASS_FIELDS.TIME]: [null],
  [HALLPASS_FIELDS.APPROVED_BY]: [null],
});

export enum CONSEQUENCE_FIELDS {
  NOTE = 'note',
  DATE = 'date',
}
export const CONSEQUENCE_GROUP = () => ({
  [CONSEQUENCE_FIELDS.NOTE]: [null, [Validators.maxLength(180)]],
  [CONSEQUENCE_FIELDS.DATE]: [null],
});

export enum BEHAVIOR_FIELDS {
  NOTE = 'note',
}
export const BEHAVIOR_GROUP = () => ({
  [BEHAVIOR_FIELDS.NOTE]: [null, [Validators.maxLength(180)]],
});

export enum CHECK_IN_FIELDS {
  NOTE = 'note',
}
export const CHECK_IN_GROUP = () => ({
  [BEHAVIOR_FIELDS.NOTE]: [null, [Validators.maxLength(180)]],
});

export const ERROR_MESSAGES = {
  [FORM_FIELDS.SELECTED_ACTION]: 'You must select an action',
  [FORM_FIELDS.SELECTED_STUDENTS]: 'You must select a student',
  [AssignmentType.HALLPASS]: 'Scheduled time is not valid',
};

export type SavedPreferences = {
  [FORM_FIELDS.LIST_FILTER]: OptionItem[];
  [FORM_FIELDS.CATEGORY]: CategoryType;
  [FORM_FIELDS.SELECTED_ACTION]: Assignment;
  // Below are ui preferences
  [FORM_FIELDS.HIDE_PICTURE_FILTER]: boolean;
  [FORM_FIELDS.SORT_COLUMN]: MyClassSortColumns;
  [FORM_FIELDS.PAGE_LAYOUT]: 'list' | 'grid';
  [FORM_FIELDS.ENABLED_SOUND]: boolean;
  [FORM_FIELDS.SHOW_BEHAVIORS]: boolean;
  [FORM_FIELDS.SHOW_CHECK_IN]: boolean;
  [FORM_FIELDS.SHOW_FLEXTIME]: boolean;
};

export const LOCKED_STATES: FormState[] = ['loading', 'submitting', 'randomizing'];

export const MOST_USED_COUNT = 4;

export enum MyClassMessages {
  PROGRESS_NEXT = 'Next',

  MOBILE_ASSIGNMENT_TITLE = 'Select type',

  NO_USERS_FOUND_TITLE = 'No users found',
  NO_USERS_FOUND_SUBTITLE = 'You need to add users to this list',

  NO_USER_LIST_SELECTED_TITLE = 'No user list selected',
  NO_USER_LIST_SELECTED_SUBTITLE = 'Select a user list to display its roster',

  NO_USER_LIST_FOUND_TITLE = 'No user list found',
  NO_USER_LIST_FOUND_SUBTITLE = 'You need to have access to a user list in order to display its roster',

  EMPTY_LIST_ACTION_BUTTON = 'Create a user list',

  FORM_LABEL_LIST = 'User list',
  FORM_LABEL_TEXT_SEARCH = 'Search all',
  FORM_LABEL_UNSELECT_ALL = 'Unselect all',
  FORM_LABEL_SELECT_ALL = 'Select all',
  FORM_LABEL_SELECT_RANDOM_DESKTOP = 'Select random',
  FORM_LABEL_SELECT_RANDOM_MOBILE = 'Random',
  FORM_LABEL_SELECT_RANDOMIZING = 'Randomizing',
  FORM_LABEL_CATEGORY = 'Category',
  FORM_LABEL_NOTE = 'Note to student (optional)',
  FORM_LABEL_PASS_DURATION = 'Pass duration (mins)',
  FORM_LABEL_APPROVED_BY = 'Approved by (optional)',
  FORM_LABEL_SCHEDULE_LATER = 'Schedule for later (optional)',
  FORM_LABEL_SCHEDULE_DATE = 'Scheduled date',
  FORM_LABEL_SCHEDULE_TIME = 'Scheduled time',
  FORM_INVALID_TIME = 'This is not a valid time',
  FORM_LABEL_DUE_DATE = 'Due date',
  FORM_LABEL_SEARCH_ACTION = 'Search action',

  ARIA_LABEL_SELECT_USER_LIST = 'Select user list',
  ARIA_LABEL_CHANGE_USER_LIST = ', change user list', //dynamic
  ARIA_LABEL_SEARCH_ALL = 'Search all students',
  ARIA_LABEL_SELECT_ALL = 'Select all students',
  ARIA_LABEL_UNSELECT_ALL = 'Unselect all students',
  ARIA_LABEL_SELECT_RANDOM = 'Select a random student',
  ARIA_LABEL_SELECT_STUDENT_OVERDUE_HALL_PASS = ', overdue hall pass', // dynamic
  ARIA_LABEL_SELECT_STUDENT_REQUESTED_HALL_PASS = ', requested hall pass', // dynamic
  ARIA_LABEL_SELECT_STUDENT_ACTIVE_HALL_PASS = ', active hall pass', // dynamic
  ARIA_LABEL_VIEW_PROFILE = 'View profile',
  ARIA_LABEL_END_PASS = 'End hall pass',
  ARIA_LABEL_APPROVE_PASS = 'Approve hall pass request',
  ARIA_LABEL_DENY_PASS = 'Deny hall pass request',
  ARIA_LABEL_OVERDUE_HALL_PASS_DETAILS = 'Overdue hall pass - view details',
  ARIA_LABEL_REQUESTED_HALL_PASS_DETAILS = 'Requested hall pass - view details',
  ARIA_LABEL_ACTIVE_HALL_PASS_DETAILS = 'Active hall pass - view details',
  ARIA_LABEL_ENDED_HALL_PASS_DETAILS = 'Ended hall pass - view details',
  ARIA_LABEL_HALL_PASS_DETAILS = 'Hall pass - view details',
  ARIA_LABEL_CHECKED_IN_DETAILS = 'Checked in - view details',
  ARIA_LABEL_NOT_CHECKED_IN_DETAILS = 'Not checked in - view details',
  ARIA_LABEL_FLEX_REGISTERED_DETAILS = 'Registered - view details',
  ARIA_LABEL_FLEX_NOT_REGISTERED_DETAILS = 'Not registered - view details',
  ARIA_LABEL_PRAISE_ASSIGNED = 'Praise assigned - ', // dynamic
  ARIA_LABEL_GUIDANCE_ASSIGNED = 'Guidance assigned - ', // dynamic
  ARIA_LABEL_SEARCH_ACTION = 'Search action',
  ARIA_LABEL_PAGE_LAYOUT = 'Page layout',
  ARIA_LABEL_INFO_FILTERS = 'Info filters',
  ARIA_LABEL_SOUND_NOTIFICATION = 'Sound notification',
  ARIA_LABEL_SETTINGS = 'Settings',
  ARIA_LABEL_ADVANCED_SETTINGS = 'Advanced settings',
  ARIA_LABEL_CATEGORY_SELECTION = 'Category selection',
  ARIA_LABEL_NOTE_TO_STUDENT = 'Note to student',
  ARIA_LABEL_ASSIGN_SELECTION = 'Assign selection',
  ARIA_LABEL_SCHEDULE_PASS_LATER = 'Schedule hall pass for later',
  ARIA_LABEL_SELECT_SCHEDULE_DATE = 'Select scheduled date',
  ARIA_LABEL_ENTER_SCHEDULE_TIME = 'Enter scheduled time',
  ARIA_LABEL_PASS_DURATION = 'Enter pass duration',
  ARIA_LABEL_SELECT_APPROVER = 'Select approver',

  HEADER_IN_CLASS = 'My Class',
  HEADER_OUT_OF_CLASS = 'Active Hall Pass',
  HEADER_ENTIRE_SCHOOL = 'Other Students',

  SETTINGS_PAGE_LAYOUT_TOOLTIP = 'Page layout',
  SETTINGS_OTHER_SETTINGS_TOOLTIP = 'Settings',
  SETTINGS_VISIBILITY_TOOLTIP = 'Info filter',
  SETTINGS_NOTIFICATIONS_TOOLTIP = 'Sound notification',

  SETTINGS_SORT_NAME = 'Sort by',
  SETTINGS_SORT_LAST_NAME = 'Last name',
  SETTINGS_SORT_FIRST_NAME = 'First name',
  SETTINGS_SORT_CHECKIN_PILL = 'Checked in',
  SETTINGS_SORT_FLEXTIME_PILL = 'FlexTime',
  SETTINGS_SORT_PRAISE_PILL = 'Praise',
  SETTINGS_SORT_GUIDANCE_PILL = 'Guidance',
  SETTINGS_DISPLAY_AVATAR = 'Display student pictures',
  SETTINGS_SOUND = 'Enable sound notifications',
  SETTINGS_SOUND_TOOLTIP = 'Enable an audible notification when a pass is overdue, a student creates a pass, or when a pass is requested from you',
  SETTINGS_MOST_USED_LIST = 'Show most used per list',
  SETTINGS_MOST_USED_LIST_TOOLTIP = 'Show most used per list tooltip',
  SETTINGS_SHOW_BEHAVIORS = 'Show behaviors',
  SETTINGS_SHOW_BEHAVIORS_TOOLTIP = 'Show how many praises and guidances/consequences are assigned with My Class during that session (resets on page reload)',
  SETTINGS_SHOW_CHECK_IN = 'Show check in',
  SETTINGS_SHOW_CHECK_IN_TOOLTIP = 'Show if a student was checked in via a check in reason with My Class or via a FlexTime check in (resets at the end of the day)',
  SETTINGS_SHOW_FLEXTIME = 'Show next FlexTime activity',
  SETTINGS_SHOW_FLEXTIME_TOOLTIP = 'Show if a student is registered to an activity for the next FlexTime period scheduled',

  ASSIGN_BTN = 'Assign',
  NEXT_BTN = 'Next',
  CONTINUE_BTN = 'Continue',
  CONTINUE_CHECK_IN = 'Check in',
  CONTINUE_CHECK_IN_TARDY = 'Check in tardy',

  SHOW_TOOLBAR = 'Show toolbar',
  HIDE_TOOLBAR = 'Hide toolbar',

  FETCH_ACTIONS_ERROR = 'There was an error fetching your action types, please try again',
  FETCH_STUDENTS_ERROR = 'There was an error fetching your student list, please try again.',
  FETCH_HALL_PASSES_ERROR = 'There was an error fetching hall passes, please try again',

  ASSIGNING_ACTION_ERROR = "There's was an issue assigning action, please try again",
  FETCHING_LISTS_ERROR = 'There was an error fetching your user lists, please try again',

  MOST_USED_EMPTY_TITLE = 'Nothing here yet!',
  MOST_USED_EMPTY_SUBTITLE = 'Most frequently assigned types will be shown here',

  SELECT_CATEGORY_MOST_USED = 'Most used',
  SELECT_CATEGORY_MOST_USED_BY_LIST = 'Most used with list',
  SELECT_CATEGORY_CONSEQUENCE = 'Consequence',
  SELECT_CATEGORY_GUIDANCE = 'Guidance',
  SELECT_CATEGORY_PRAISE = 'Praise',
  SELECT_CATEGORY_HALLPASS = 'Hall pass',
  SELECT_CATEGORY_COMMUNICATION = 'Communication',
  SELECT_CATEGORY_FLEX_TIME = 'FlexTime',
  SELECT_CATEGORY_CHECK_IN = 'Check in reason',

  TOOLTIP_APPROVE_PASS = 'Approve hall pass',
  TOOLTIP_DENY_PASS = 'Deny hall pass',
}

const OptionItemSchema = z.object({
  label: z.string().catch(''),
  value: z.number().catch(0),
  data: z.any().nullable(),
  category: z.object({
    value: z.string().catch(''),
  }),
});

export const myClassQueryParamSchema = z.object({
  lists: z.array(OptionItemSchema).catch([]),
});

export type MyClassQueryParams = z.infer<typeof myClassQueryParamSchema>;

export const queryParamsToMyClassFilters = (params: MyClassQueryParams) => ({
  lists: params.lists,
});
